// Within reach of the screen (GLOSSARY.md "In-view hydration"): any part of
// an element inside the viewport or the half viewport below it. One observer
// watches every element a grid registers; each is reported once, the first
// time it comes within reach, and then forgotten.
export interface WithinReach {
  // Calls onReach the first time element comes within reach; the returned
  // function stops watching it.
  observe: (element: Element, onReach: () => void) => () => void
}

export function createWithinReach(): WithinReach {
  const callbacks = new Map<Element, () => void>()
  let observer: IntersectionObserver | undefined
  const observerOf = () =>
    (observer ??= new IntersectionObserver(
      (entries, self) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const onReach = callbacks.get(entry.target)
          callbacks.delete(entry.target)
          self.unobserve(entry.target)
          onReach?.()
        }
      },
      { rootMargin: '0px 0px 50% 0px', threshold: 0 },
    ))

  return {
    observe: (element, onReach) => {
      callbacks.set(element, onReach)
      observerOf().observe(element)
      return () => {
        callbacks.delete(element)
        observer?.unobserve(element)
      }
    },
  }
}
